import React, { useState, useMemo } from 'react';
import HrnbButton from '../controls/HrnbButton';
import HrnbSelect from '../controls/HrnbSelect';

const EVENT_TYPE_OPTIONS = [
    { value: '', label: 'Все типы' },
    { value: 'BATTLE_START', label: 'Баттл' },
    { value: 'ROUND_START', label: 'Раунд' },
    { value: 'VOTING_START', label: 'Голосование' },
    { value: 'REGISTRATION_DEADLINE', label: 'Дедлайн регистрации' },
    { value: 'TRACK_UPLOAD_DEADLINE', label: 'Дедлайн загрузки' },
    { value: 'LIVE_STREAM', label: 'Стрим' },
    { value: 'MEETING', label: 'Встреча' },
    { value: 'CUSTOM', label: 'Другое' },
];

const STATUS_OPTIONS = [
    { value: '', label: 'Все статусы' },
    { value: 'PUBLISHED', label: 'Опубликовано' },
    { value: 'DRAFT', label: 'Черновик' },
    { value: 'CANCELLED', label: 'Отменено' },
];

export default function EventList({ events = [], onSelectEvent, loading, page = 0, pageSize = 20, totalElements = 0, onPageChange }) {
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [sortBy, setSortBy] = useState('startTime');

    const filtered = useMemo(() => {
        let result = [...events];
        if (filterType) {
            result = result.filter((e) => e.eventType === filterType);
        }
        if (filterStatus) {
            result = result.filter((e) => e.status === filterStatus);
        }
        result.sort((a, b) => {
            const da = new Date(a[sortBy] || a.startTime);
            const db = new Date(b[sortBy] || b.startTime);
            return da - db;
        });
        return result;
    }, [events, filterType, filterStatus, sortBy]);

    const totalPages = Math.ceil(filtered.length / pageSize);
    const paginated = filtered.slice(page * pageSize, (page + 1) * pageSize);

    if (loading) {
        return <div className="event-list-loading">Загрузка событий...</div>;
    }

    return (
        <div className="event-list">
            <div className="event-list-filters">
                <HrnbSelect
                    value={filterType}
                    onChange={(val) => { setFilterType(val); }}
                    options={EVENT_TYPE_OPTIONS}
                    placeholder="Тип события"
                />
                <HrnbSelect
                    value={filterStatus}
                    onChange={(val) => { setFilterStatus(val); }}
                    options={STATUS_OPTIONS}
                    placeholder="Статус"
                />
                <HrnbSelect
                    value={sortBy}
                    onChange={setSortBy}
                    options={[
                        { value: 'startTime', label: 'По дате начала' },
                        { value: 'title', label: 'По названию' },
                    ]}
                    placeholder="Сортировка"
                />
            </div>

            {paginated.length === 0 ? (
                <div className="event-list-empty">События не найдены</div>
            ) : (
                <>
                    <div className="event-list-items">
                        {paginated.map((event) => (
                            <EventCard key={event.id} event={event} onClick={() => onSelectEvent && onSelectEvent(event)} />
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="event-list-pagination">
                            <HrnbButton
                                variant="outline"
                                disabled={page === 0}
                                onClick={() => onPageChange && onPageChange(page - 1)}
                            >
                                Назад
                            </HrnbButton>
                            <span className="pagination-info">
                                Страница {page + 1} из {totalPages} ({totalElements} всего)
                            </span>
                            <HrnbButton
                                variant="outline"
                                disabled={page + 1 >= totalPages}
                                onClick={() => onPageChange && onPageChange(page + 1)}
                            >
                                Вперёд
                            </HrnbButton>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function EventCard({ event, onClick }) {
    const start = parseDate(event.startTime);
    const end = parseDate(event.endTime);

    return (
        <button className="event-card" onClick={onClick} type="button">
            <div className="event-card-date">
                <div className="event-card-day">{start ? start.getDate() : '?'}</div>
                <div className="event-card-month">{start ? MONTH_SHORT[start.getMonth()] : ''}</div>
            </div>
            <div className="event-card-body">
                <div className="event-card-header">
                    <span className={`event-card-type event-card-type--${(event.eventType || 'CUSTOM').toLowerCase()}`}>
                        {getEventTypeLabel(event.eventType)}
                    </span>
                    {event.status && (
                        <span className={`event-card-status event-card-status--${event.status.toLowerCase()}`}>
                            {getStatusLabel(event.status)}
                        </span>
                    )}
                </div>
                <h4 className="event-card-title">{event.title}</h4>
                {event.description && (
                    <p className="event-card-desc">{event.description}</p>
                )}
                <div className="event-card-meta">
                    <span>{formatDateTime(start)}</span>
                    {end && <span> — {formatDateTime(end)}</span>}
                </div>
            </div>
        </button>
    );
}

const MONTH_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

function parseDate(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
}

function formatDateTime(date) {
    if (!date) return '';
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getEventTypeLabel(type) {
    const labels = {
        BATTLE_START: 'Баттл',
        ROUND_START: 'Раунд',
        ROUND_END: 'Раунд',
        VOTING_START: 'Голосование',
        VOTING_END: 'Голосование',
        REGISTRATION_DEADLINE: 'Регистрация',
        TRACK_UPLOAD_DEADLINE: 'Загрузка',
        LIVE_STREAM: 'Стрим',
        MEETING: 'Встреча',
        CUSTOM: 'Другое',
    };
    return labels[type] || type || 'Другое';
}

function getStatusLabel(status) {
    const labels = {
        PUBLISHED: 'Опубликовано',
        DRAFT: 'Черновик',
        CANCELLED: 'Отменено',
    };
    return labels[status] || status;
}
